import { useMemo, useRef, useState, type DragEvent } from 'react';
import clsx from 'clsx';
import { PageShell, PageHeader, SectionHeading } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input, Textarea } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { Pagination, usePagination } from '../../components/ui/Pagination';
import { ChevronRight, Upload } from 'lucide-react';
import { formatDayMonthTime } from '../../utils/dateFormat';
import { useBrandingRuns, useCancelBrandingRun, useStartBrandingRun } from '../../hooks/useDocBranding';
import {
  downloadBrandedPdf,
  isRunActive,
  pagePreviewUrl,
  type DocBrandingFinding,
  type DocBrandingRun,
} from '../../api/docBranding';

const ACCEPT = '.docx,.pptx,.pdf,.md,.txt';

const SOURCE_LABEL: Record<DocBrandingFinding['source'], string> = {
  layout: 'Layout',
  brand: 'Brand',
  content: 'Content',
  visual: 'Design review',
  fidelity: 'Fidelity',
};

/** "Q3 board pack.docx" → "Q3 board pack": the extension is noise in a list of documents. */
function displayName(filename: string): string {
  return filename.replace(/\.(docx|pptx|pdf|md|txt)$/i, '');
}

/**
 * One group per document: every run with the same title, newest first. Retries
 * and later uploads of one document are its versions, not new documents.
 */
function groupByDocument(runs: DocBrandingRun[]): DocBrandingRun[][] {
  const groups = new Map<string, DocBrandingRun[]>();
  for (const run of runs) {
    const key = displayName(run.sourceFilename).trim().toLowerCase();
    groups.set(key, [...(groups.get(key) ?? []), run]);
  }
  return [...groups.values()].map((g) => [...g].sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
}

/**
 * A finding that makes the PDF wrong to send: words missing from or not
 * supported by the original, a claim the kit forbids, or text running off the
 * page. Underfilled pages and design-review notes are things to check, not fix.
 */
function isBlocking(f: DocBrandingFinding): boolean {
  return f.severity === 'error' && (f.source === 'fidelity' || f.source === 'content' || f.code.startsWith('overflow'));
}

function formatBytes(bytes: number): string {
  return bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}


function UploadCard() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [documentLabel, setDocumentLabel] = useState('');
  const [dateLabel, setDateLabel] = useState('');
  const [confidential, setConfidential] = useState(true);
  const [note, setNote] = useState('');
  const start = useStartBrandingRun();

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) setFile(dropped);
  };

  const submit = () => {
    if (!file) return;
    start.mutate(
      {
        file,
        confidential,
        ...(documentLabel.trim() ? { documentLabel: documentLabel.trim() } : {}),
        ...(dateLabel.trim() ? { dateLabel: dateLabel.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      },
      {
        onSuccess: () => {
          setFile(null);
          setNote('');
          if (inputRef.current) inputRef.current.value = '';
        },
      },
    );
  };

  return (
    // The card itself is the drop target: one surface, not a dashed box inside a card.
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={onDrop}
      className={clsx(
        'rounded-2xl border bg-surface-primary p-8 transition-colors',
        dragging ? 'border-dashed border-text-primary bg-surface-secondary' : 'border-border-light',
      )}
    >
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex w-full items-center gap-4 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
        >
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-surface-secondary text-text-secondary">
            <Upload className="h-4 w-4" aria-hidden />
          </span>
          {file ? (
            <span className="min-w-0 space-y-0.5">
              <span className="block truncate text-body-md font-medium text-text-primary">{file.name}</span>
              <span className="block text-body-sm text-text-secondary">{formatBytes(file.size)} · click to choose another file</span>
            </span>
          ) : (
            <span className="min-w-0 space-y-0.5">
              <span className="block text-body-md font-medium text-text-primary">Drop a document anywhere on this card, or click to choose one</span>
              <span className="block text-body-sm text-text-secondary">Word, PowerPoint, PDF, Markdown or text · up to 20 MB</span>
            </span>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />

        <button
          type="button"
          aria-expanded={showOptions}
          className="inline-flex items-center gap-1 rounded-sm text-body-sm font-medium text-text-secondary hover:text-text-primary"
          onClick={() => setShowOptions((v) => !v)}
        >
          <ChevronRight className={clsx('h-4 w-4 transition-transform', showOptions && 'rotate-90')} aria-hidden />
          Options
          <span className="font-normal text-text-tertiary">· running title, footer date, notes</span>
        </button>
        {showOptions && (
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Running title"
              placeholder="Worked out from the document"
              hint='The document type on the cover, e.g. "Build or buy analysis"'
              value={documentLabel}
              onChange={(e) => setDocumentLabel(e.target.value)}
              maxLength={60}
            />
            <Input
              label="Date in the footer"
              placeholder="This month"
              value={dateLabel}
              onChange={(e) => setDateLabel(e.target.value)}
              maxLength={40}
            />
            <label className="flex items-center gap-2 text-body-sm text-text-primary">
              <input type="checkbox" checked={confidential} onChange={(e) => setConfidential(e.target.checked)} />
              Mark as Confidential in the footer
            </label>
            <div className="md:col-span-2">
              <Textarea
                label="Anything the designer should know"
                placeholder="Not needed. For example: this goes to a CFO, keep the pricing table on one page."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={1000}
                rows={2}
              />
            </div>
          </div>
        )}

        {start.error && <Alert variant="error">{start.error.message}</Alert>}

        <div className="flex items-center justify-between gap-4">
          <p className="text-body-sm text-text-tertiary">
            {file ? 'Takes about 5 to 15 minutes.' : 'Choose a document above to start. Takes about 5 to 15 minutes.'}
          </p>
          <Button
            onClick={submit}
            disabled={!file}
            isLoading={start.isPending}
            title={file ? undefined : 'Choose a document first'}
            className="disabled:!opacity-40"
          >
            Make it on-brand
          </Button>
        </div>
      </div>
    </div>
  );
}

function FindingsList({ findings }: { findings: DocBrandingFinding[] }) {
  const sorted = [...findings].sort((a, b) =>
    isBlocking(a) === isBlocking(b) ? a.page - b.page : isBlocking(a) ? -1 : 1,
  );
  return (
    <ul className="space-y-2">
      {sorted.map((f, i) => (
        <li key={i} className="flex gap-3 text-body-sm">
          {isBlocking(f) ? <Badge variant="error">Fix</Badge> : <Badge>Check</Badge>}
          <span className="text-text-secondary">
            <span className="font-medium text-text-primary">
              {f.page > 0 ? `Page ${f.page}` : f.specIndex >= 0 ? `Page ${f.specIndex + 1} (planned)` : 'Document'} ·{' '}
              {SOURCE_LABEL[f.source]}
            </span>{' '}
            {f.message}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** One document: a row in the shared Recent documents card (earlier runs nest under it). */
function RunRow({
  run,
  earlier = [],
  version,
}: {
  run: DocBrandingRun;
  earlier?: DocBrandingRun[];
  /** Set when the document has several versions: which one this is, oldest = v1. */
  version?: { n: number; latest: boolean };
}) {
  const [open, setOpen] = useState(false);
  const [showEarlier, setShowEarlier] = useState(false);
  const [showIssues, setShowIssues] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const cancel = useCancelBrandingRun();
  const active = isRunActive(run.status);
  const errors = run.findings.filter(isBlocking).length;
  const warnings = run.findings.length - errors;

  // One rule for every ready document: a red "N to fix" pill when the PDF is
  // wrong to send, and a neutral "N to check" pill whenever Check items are
  // open — each pill counts the same findings the list below labels Fix / Check.
  const statusBadge =
    run.status === 'ready' ? (
      errors > 0 || warnings > 0 ? (
        <span className="flex flex-none items-center gap-1.5">
          {errors > 0 && <Badge variant="error">{errors} to fix</Badge>}
          {warnings > 0 && <Badge>{warnings} to check</Badge>}
        </span>
      ) : null
    ) : run.status === 'failed' ? (
      <Badge variant="error">Failed</Badge>
    ) : run.status === 'cancelled' ? (
      <Badge>Cancelled</Badge>
    ) : (
      <Badge variant="info">In progress</Badge>
    );

  return (
    <div className="px-6 py-5">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2">
              <p className="truncate text-body-md font-medium text-text-primary" title={run.sourceFilename}>
                {displayName(run.sourceFilename)}
              </p>
              {version && (
                <span className="flex-none text-body-xs text-text-tertiary" title="This document was run more than once">
                  v{version.n}
                  {version.latest ? ' · latest' : ''}
                </span>
              )}
              {statusBadge}
            </div>
            <p className="text-body-sm text-text-secondary">
              {run.requestedByName ?? 'Someone'} · {formatDayMonthTime(run.createdAt)}
              {run.pageCount ? ` · ${run.pageCount} pages` : ''}
            </p>
            {active && (
              <p className="flex items-center gap-2 text-body-sm text-text-secondary">
                <Spinner size="sm" /> {run.progressMessage ?? 'Working'}
              </p>
            )}
          </div>
          <div className="flex flex-none items-center gap-2">
            {active && (
              <Button variant="ghost" size="sm" onClick={() => cancel.mutate(run.id)} isLoading={cancel.isPending}>
                Cancel
              </Button>
            )}
            {run.status === 'ready' && (
              <>
                {/* Row actions stay quiet: "Make it on-brand" is the page's one primary action */}
                <Button variant="ghost" size="sm" onClick={() => setOpen((v) => !v)}>
                  {open ? 'Hide pages' : 'View pages'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setDownloadError(null);
                    downloadBrandedPdf(run).catch((e: unknown) =>
                      setDownloadError(e instanceof Error ? e.message : 'Download failed'),
                    );
                  }}
                >
                  Download PDF
                </Button>
              </>
            )}
          </div>
        </div>

        {run.status === 'failed' && run.errorMessage && <Alert variant="error">{run.errorMessage}</Alert>}
        {downloadError && <Alert variant="error">{downloadError}</Alert>}
        {run.sourceNotes.length > 0 && (
          <Alert variant="warning" title="Not carried over from the original">
            <ul className="list-disc pl-4">
              {run.sourceNotes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </Alert>
        )}

        {run.status === 'ready' && run.findings.length > 0 && (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setShowIssues((v) => !v)}
              aria-expanded={showIssues}
              className="flex items-center gap-1 text-body-sm font-medium text-text-secondary hover:text-text-primary"
            >
              <ChevronRight className={clsx('h-4 w-4 transition-transform', showIssues && 'rotate-90')} aria-hidden />
              {/* The count is already in the badge beside the title */}
              {showIssues ? 'Hide issues' : 'Show issues'}
            </button>
            {showIssues && (
              <>
                {errors > 0 && (
                  <p className="text-body-xs text-text-tertiary">
                    Still open after the automatic fixes: look at these before sending.
                  </p>
                )}
                <FindingsList findings={run.findings} />
              </>
            )}
          </div>
        )}

        {earlier.length > 0 && (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setShowEarlier((v) => !v)}
              aria-expanded={showEarlier}
              className="flex items-center gap-1 text-body-xs text-text-tertiary hover:text-text-primary"
            >
              <ChevronRight className={clsx('h-3.5 w-3.5 transition-transform', showEarlier && 'rotate-90')} aria-hidden />
              {earlier.length} earlier version{earlier.length === 1 ? '' : 's'}
            </button>
            {showEarlier && (
              <div className="divide-y divide-border-light rounded-xl border border-border-light [&>div]:px-4 [&>div]:py-4">
                {earlier.map((r, i) => (
                  <RunRow key={r.id} run={r} version={{ n: earlier.length - i, latest: false }} />
                ))}
              </div>
            )}
          </div>
        )}

        {open && run.status === 'ready' && (
          <div className="space-y-3">
            {run.rounds.length > 0 && (
              <p className="text-body-sm text-text-secondary">
                {run.rounds
                  .map((r) => `Round ${r.round}: ${r.errors} error${r.errors === 1 ? '' : 's'}, ${r.warnings} warning${r.warnings === 1 ? '' : 's'}`)
                  .join(' → ')}
              </p>
            )}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {run.previewKeys.map((_, i) => (
                <a key={i} href={pagePreviewUrl(run.id, i + 1)} target="_blank" rel="noreferrer" className="block">
                  <img
                    src={pagePreviewUrl(run.id, i + 1)}
                    alt={`Page ${i + 1}`}
                    loading="lazy"
                    className="w-full rounded-lg border border-border-light shadow-sm"
                  />
                  <p className="mt-1 text-center text-body-xs text-text-secondary">Page {i + 1}</p>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const NO_RUNS: DocBrandingRun[] = [];

export function Documents() {
  const { data, isLoading, error, refetch } = useBrandingRuns();
  const runs = data?.runs ?? NO_RUNS;
  // One row per document; its earlier versions nest inside it.
  const grouped = useMemo(() => groupByDocument(runs), [runs]);
  const { pageItems, page, pageCount, setPage, total, pageSize } = usePagination(grouped, 10);

  return (
    <PageShell>
      <PageHeader eyebrow="Company"
        title="Brand docs"
        subtitle="Drop in any document and get it back rebuilt on the SEQUESTO document kit."
        info="The document is rebuilt on the SEQUESTO document kit, one topic per page. Every page is then measured and design-reviewed, and the words are checked against your original and against the copy and claims rules of the kit."
      />
      <UploadCard />
      <SectionHeading>Recent brand docs</SectionHeading>
      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState title="Couldn’t load brand docs" error={error} onRetry={() => refetch()} />
      ) : runs.length ? (
        <div className="space-y-3">
          <Card padding="none" className="divide-y divide-border-light">
            {pageItems.map(([latest, ...earlier]) => (
              <RunRow
                key={latest!.id}
                run={latest!}
                earlier={earlier}
                version={earlier.length > 0 ? { n: earlier.length + 1, latest: true } : undefined}
              />
            ))}
          </Card>
          <Pagination page={page} pageCount={pageCount} total={total} pageSize={pageSize} onPage={setPage} />
          {data?.truncated && <p className="text-body-xs text-text-tertiary">Showing the most recent 200.</p>}
        </div>
      ) : (
        <EmptyState title="No brand docs yet" description="The first document you drop in shows up here." />
      )}
    </PageShell>
  );
}
